import { Quote } from '~/lib/types/basket-types';
import { Workflow } from '../types/workflow-types';
import { isDriverBringsLabels, isPrintInStore, isQuoteDropOff, isQuoteShopDelivery } from '../quotes';

/* tslint:disable:no-unused-variable */
export const GenerateWorkflow = (
  _isXs: boolean,
  requiresCustoms: boolean,
  quote?: Quote | undefined | null,
  currentItemAction?: string | null | undefined
): Workflow => {
  const hasPrintInStore = quote ? isPrintInStore(quote) : false;
  const hasDriverBringsLabels = quote ? isDriverBringsLabels(quote) : false;
  const isDropOff = quote ? isQuoteDropOff(quote) : false;
  const isShopDelivery = quote ? isQuoteShopDelivery(quote) : false;
  const includeAccount = currentItemAction !== 'duplicate-service';

  if (currentItemAction === 'drop-in') {
    return requiresCustoms
      ? DropInDesktopInternationalWorkFlow(
          hasPrintInStore,
          isDropOff,
          hasDriverBringsLabels,
          isShopDelivery,
          includeAccount
        )
      : DropInDesktopDomesticWorkFlow(
          hasPrintInStore,
          isDropOff,
          hasDriverBringsLabels,
          isShopDelivery,
          includeAccount
        );
  }

  if (!requiresCustoms) {
    if (currentItemAction === 'duplicate' || currentItemAction === 'split') {
      return DesktopDomesticDuplicateWorkFlow();
    }

    if (currentItemAction === 'duplicate-service') {
      return DesktopDomesticDuplicateServiceWorkFlow();
    }

    if (currentItemAction === 'Parcels') {
      return DesktopEditParcelsWorkFlow();
    }

    if (currentItemAction === 'Origin') {
      return DesktopEditAddressWorkFlow(hasDriverBringsLabels, isDropOff, isShopDelivery);
    }

    if (currentItemAction === 'DropLocations') {
      return DesktopEditDropshopWorkFlow();
    }

    if (currentItemAction === 'DestinationPickUpLocations') {
      return DesktopEditDestinationPickUpLocationsWorkFlow();
    }

    if (currentItemAction === 'CollectionDate') {
      return DesktopDomesticEditCollectionDateWorkFlow();
    }
  }

  return requiresCustoms
    ? DesktopInternationalWorkFlow(hasPrintInStore, isDropOff, hasDriverBringsLabels, isShopDelivery, includeAccount)
    : DesktopDomesticWorkFlow(hasPrintInStore, isDropOff, hasDriverBringsLabels, isShopDelivery, includeAccount);
};

const DesktopInternationalWorkFlow = (
  hasPrintInStore: boolean,
  isDropOff: boolean,
  hasDriverBringsLabel: boolean,
  isShopDelivery: boolean,
  includeAccount: boolean
) => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-CUSTOMS'
  };
  if (isDropOff) {
    output.screens.push({
      path: 'address',
      modules: hasPrintInStore
        ? ['Account', 'Origin', 'PrintInStore', 'DropLocations', 'Destination']
        : ['Account', 'Origin', 'DropLocations', 'Destination'],
      onSubmitActions: ['COMMS', 'SAVE-SENDER-ADDRESS']
    });
  } else {
    output.screens.push({
      path: 'address',
      modules: hasDriverBringsLabel
        ? ['Account', 'Origin', 'Destination', 'DriverBringsLabels']
        : ['Account', 'Origin', 'Destination'],
      onSubmitActions: ['COMMS', 'SAVE-SENDER-ADDRESS']
    });
  }
  if (isDropOff) {
    output.screens.push({
      path: 'parcel',
      modules: ['ExportReason', 'ParcelContents', 'Protection', 'Duties', 'AgreeTerms'],
      onSubmitActions: []
    });
  } else {
    output.screens.push({
      path: 'parcel',
      modules: ['ExportReason', 'ParcelContents', 'CollectionDate', 'Protection', 'Duties', 'AgreeTerms'],
      onSubmitActions: []
    });
  }
  // Remove account screen if not required.
  if (!includeAccount) {
    output.screens.forEach((screen) => {
      if (screen.modules.includes('Account')) {
        screen.modules = screen.modules.filter((module) => module !== 'Account');
      }
    });
  }
  // Add DropLocations module if shop delivery is selected.
  if (isShopDelivery) {
    AppendShopDeliveryModule(output);
  }

  // Sender Type
  output.screens[output.screens.length - 1].onSubmitActions.push('SENDER-TYPE');

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopDomesticWorkFlow = (
  hasPrintInStore: boolean,
  isDropOff: boolean,
  hasDriverBringsLabel: boolean,
  isShopDelivery: boolean,
  includeAccount: boolean
) => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP'
  };
  if (isDropOff) {
    output.screens.push({
      path: 'address',
      modules: hasPrintInStore
        ? ['Account', 'Origin', 'PrintInStore', 'DropLocations', 'Destination']
        : ['Account', 'Origin', 'DropLocations', 'Destination'],
      onSubmitActions: ['COMMS', 'SAVE-SENDER-ADDRESS']
    });
  } else {
    output.screens.push({
      path: 'address',
      modules: hasDriverBringsLabel
        ? ['Account', 'Origin', 'Destination', 'DriverBringsLabels']
        : ['Account', 'Origin', 'Destination'],
      onSubmitActions: ['COMMS', 'SAVE-SENDER-ADDRESS']
    });
  }
  if (isDropOff) {
    output.screens.push({
      path: 'parcel',
      modules: ['Parcels', 'Protection', 'AgreeTerms'],
      onSubmitActions: []
    });
  } else {
    output.screens.push({
      path: 'parcel',
      modules: ['Parcels', 'CollectionDate', 'Protection', 'AgreeTerms'],
      onSubmitActions: []
    });
  }
  // Remove account screen if not required.
  if (!includeAccount) {
    output.screens.forEach((screen) => {
      if (screen.modules.includes('Account')) {
        screen.modules = screen.modules.filter((module) => module !== 'Account');
      }
    });
  }
  // Add DropLocations module if shop delivery is selected.
  if (isShopDelivery) {
    AppendShopDeliveryModule(output);
  }

  // Sender Type
  output.screens[output.screens.length - 1].onSubmitActions.push('SENDER-TYPE');

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DropInDesktopInternationalWorkFlow = (
  hasPrintInStore: boolean,
  isDropOff: boolean,
  hasDriverBringsLabel: boolean,
  isShopDelivery: boolean,
  includeAccount: boolean
) => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-CUSTOMS'
  };
  if (isDropOff) {
    output.screens.push({
      path: 'address',
      modules: hasPrintInStore ? ['PrintInStore', 'DropLocations', 'Destination'] : ['DropLocations', 'Destination'],
      onSubmitActions: []
    });
  } else if (hasDriverBringsLabel) {
    output.screens.push({
      path: 'address',
      modules: ['DriverBringsLabels', 'Destination'],
      onSubmitActions: []
    });
  } else {
    output.screens.push({
      path: 'address',
      modules: ['Destination'],
      onSubmitActions: []
    });
  }
  if (isDropOff) {
    output.screens.push({
      path: 'parcel',
      modules: ['ExportReason', 'ParcelContents', 'Protection', 'Duties', 'AgreeTerms'],
      onSubmitActions: []
    });
  } else {
    output.screens.push({
      path: 'parcel',
      modules: ['ExportReason', 'ParcelContents', 'CollectionDate', 'Protection', 'Duties', 'AgreeTerms'],
      onSubmitActions: []
    });
  }
  // Remove account screen if not required.
  if (!includeAccount) {
    output.screens.forEach((screen) => {
      if (screen.modules.includes('Account')) {
        screen.modules = screen.modules.filter((module) => module !== 'Account');
      }
    });
  }
  // Add DropLocations module if shop delivery is selected.
  if (isShopDelivery) {
    AppendShopDeliveryModule(output);
  }

  // Sender Type
  output.screens[output.screens.length - 1].onSubmitActions.push('SENDER-TYPE');

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DropInDesktopDomesticWorkFlow = (
  hasPrintInStore: boolean,
  isDropOff: boolean,
  hasDriverBringsLabel: boolean,
  isShopDelivery: boolean,
  includeAccount: boolean
) => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP'
  };
  if (isDropOff) {
    output.screens.push({
      path: 'address',
      modules: hasPrintInStore ? ['PrintInStore', 'DropLocations', 'Destination'] : ['DropLocations', 'Destination'],
      onSubmitActions: []
    });
  } else if (hasDriverBringsLabel) {
    output.screens.push({
      path: 'address',
      modules: ['DriverBringsLabels', 'Destination'],
      onSubmitActions: []
    });
  } else {
    output.screens.push({
      path: 'address',
      modules: ['Destination'],
      onSubmitActions: []
    });
  }
  if (isDropOff) {
    output.screens.push({
      path: 'parcel',
      modules: ['Parcels', 'Protection', 'AgreeTerms'],
      onSubmitActions: []
    });
  } else {
    output.screens.push({
      path: 'parcel',
      modules: ['Parcels', 'CollectionDate', 'Protection', 'AgreeTerms'],
      onSubmitActions: []
    });
  }
  // Remove account screen if not required.
  if (!includeAccount) {
    output.screens.forEach((screen) => {
      if (screen.modules.includes('Account')) {
        screen.modules = screen.modules.filter((module) => module !== 'Account');
      }
    });
  }
  // Add DropLocations module if shop delivery is selected.
  if (isShopDelivery) {
    AppendShopDeliveryModule(output);
  }

  // Sender Type
  output.screens[output.screens.length - 1].onSubmitActions.push('SENDER-TYPE');

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const AppendShopDeliveryModule = (workflow: Workflow): Workflow => {
  workflow.screens.forEach((screen) => {
    if (screen.modules.includes('Destination')) {
      // Add DestinationPickUpLocations module after Destination module.
      const index = screen.modules.indexOf('Destination');
      screen.modules.splice(index + 1, 0, 'DestinationPickUpLocations');
    }
  });
  return workflow;
};

const DesktopDomesticDuplicateWorkFlow = () => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-DUPLICATE'
  };

  output.screens.push({
    path: 'address',
    modules: ['ParcelsSummary', 'Protection', 'Destination'],
    onSubmitActions: []
  });

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopDomesticDuplicateServiceWorkFlow = () => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-DUPLICATE-SERVICE'
  };

  output.screens.push({
    path: 'address',
    modules: ['Destination'],
    onSubmitActions: []
  });

  output.screens.push({
    path: 'parcel',
    modules: ['Parcels', 'Protection', 'AgreeTerms'],
    onSubmitActions: []
  });

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopDomesticEditCollectionDateWorkFlow = () => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-DUPLICATE'
  };

  output.screens.push({
    path: 'address',
    modules: ['CollectionDate'],
    onSubmitActions: []
  });

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopEditDropshopWorkFlow = () => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-CHANGE-DROPSHOP'
  };

  output.screens.push({
    path: 'address',
    modules: ['DropLocations'],
    onSubmitActions: []
  });

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopEditDestinationPickUpLocationsWorkFlow = () => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-CHANGE-COLLECT-LOCATIONS'
  };

  output.screens.push({
    path: 'address',
    modules: ['DestinationPickUpLocations'],
    onSubmitActions: []
  });

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopEditParcelsWorkFlow = () => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-CHANGE-DROPSHOP'
  };

  output.screens.push({
    path: 'parcel',
    modules: ['Parcels', 'Protection'],
    onSubmitActions: []
  });

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const DesktopEditAddressWorkFlow = (hasDriverBringsLabel: boolean, isDropOff: boolean, isShopDelivery: boolean) => {
  const output: Workflow = {
    screens: [],
    name: 'DEFAULT-DESKTOP-CHANGE-DROPSHOP'
  };

  output.screens.push({
    path: 'address',
    modules: hasDriverBringsLabel
      ? ['Origin', 'Destination', 'DriverBringsLabels']
      : isDropOff
        ? ['Origin', 'DropLocations', 'Destination']
        : ['Origin', 'Destination'],
    onSubmitActions: ['SAVE-SENDER-ADDRESS']
  });

  // Add DropLocations module if shop delivery is selected.
  if (isShopDelivery) {
    AppendShopDeliveryModule(output);
  }

  // Set confirm as final stage in the process.
  output.screens[output.screens.length - 1].onSubmitActions.push('BASKET');

  // Pad out the stepper to show the two additional steps not covered by order process.
  output.screens.push({ path: 'confirm', modules: [], onSubmitActions: [] });
  output.screens.push({ path: 'payment', modules: [], onSubmitActions: [] });

  return AssignNextAndBack(output);
};

const AssignNextAndBack = (workflow: Workflow): Workflow => {
  // Assign next and back paths to each screen.
  for (let i = 0; i < workflow.screens.length; i++) {
    const screen = workflow.screens[i];
    screen.index = i;
    screen.next = screen.next ?? workflow.screens[i + 1]?.path;
    screen.back = screen.back ?? workflow.screens[i - 1]?.path;
  }

  // Add basket to the final screen if it doesn't already exist.
  if (!workflow.screens.some((x) => x.onSubmitActions.includes('PAY') || x.onSubmitActions.includes('BASKET'))) {
    workflow.screens[workflow.screens.length - 1]?.onSubmitActions.push('BASKET');
  }

  return workflow;
};

export const GenerateContentModuleKey = (key: string) => `Content:${key}`;
